import { type AgentSessionResponse } from '@air/server-sdk';

import {
  WORKSPACE_AGENT_MY_TASKS_COMPACT_ROW_HEIGHT,
  WORKSPACE_AGENT_MY_TASKS_COMPACT_ROW_TOTAL_HEIGHT,
  WORKSPACE_AGENT_MY_TASKS_LOAD_MORE_ROW_HEIGHT,
  WORKSPACE_AGENT_MY_TASKS_ROW_HEIGHT,
  WORKSPACE_AGENT_MY_TASKS_ROW_TOTAL_HEIGHT,
  WORKSPACE_AGENT_MY_TASKS_SECTION_HEADER_HEIGHT,
  WORKSPACE_AGENT_MY_TASKS_SECTION_HEADER_WITH_GAP_HEIGHT,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentMyTasksRowLayout';
import {
  classifyWorkspaceAgentSessionBucket,
  type WorkspaceAgentSessionBucket,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/classifyWorkspaceAgentSessionBucket';
import { type Automation } from '~/hooks/queries/automations/usePrivateAutomationsList';

export const WORKSPACE_AGENT_SESSION_TASK_SECTIONS: ReadonlyArray<{
  bucket: WorkspaceAgentSessionBucket;
  label: string;
}> = [
  { bucket: 'needs-response', label: 'Needs response' },
  { bucket: 'in-progress', label: 'In progress' },
  { bucket: 'all-tasks', label: 'All tasks' },
  { bucket: 'archived', label: 'Archived' },
];

export const WORKSPACE_AGENT_PANEL_MY_TASKS_LIST_AUTOMATIONS = 'WORKSPACE_AGENT_PANEL_MY_TASKS_LIST_AUTOMATIONS';

export const WORKSPACE_AGENT_PANEL_MY_TASKS_LIST_AUTOMATIONS_TOGGLE =
  'WORKSPACE_AGENT_PANEL_MY_TASKS_LIST_AUTOMATIONS_TOGGLE';

export const WORKSPACE_AGENT_PANEL_MY_TASKS_ROW_AUTOMATION = 'WORKSPACE_AGENT_PANEL_MY_TASKS_ROW_AUTOMATION';

export const WORKSPACE_AGENT_SESSION_TASK_SECTION_LIST_TEST_IDS: Record<WorkspaceAgentSessionBucket, string> = {
  'needs-response': 'WORKSPACE_AGENT_PANEL_MY_TASKS_LIST_NEEDS_RESPONSE',
  'in-progress': 'WORKSPACE_AGENT_PANEL_MY_TASKS_LIST_IN_PROGRESS',
  'all-tasks': 'WORKSPACE_AGENT_PANEL_MY_TASKS_LIST_ALL_TASKS',
  archived: 'WORKSPACE_AGENT_PANEL_MY_TASKS_LIST_ARCHIVED',
};

export const WORKSPACE_AGENT_SESSION_TASK_ROW_TEST_IDS: Record<WorkspaceAgentSessionBucket, string> = {
  'needs-response': 'WORKSPACE_AGENT_PANEL_MY_TASKS_ROW_NEEDS_RESPONSE',
  'in-progress': 'WORKSPACE_AGENT_PANEL_MY_TASKS_ROW_IN_PROGRESS',
  'all-tasks': 'WORKSPACE_AGENT_PANEL_MY_TASKS_ROW_ALL_TASKS',
  archived: 'WORKSPACE_AGENT_PANEL_MY_TASKS_ROW_ARCHIVED',
};

/** Where the local onboarding review task sits. Active is always first; archived is always last. */
export type WorkspaceAgentOnboardingReviewTaskPlacement = 'active' | 'archived';

export type WorkspaceAgentSessionVirtualRow =
  | {
      type: 'section-header';
      bucket: WorkspaceAgentSessionBucket;
      count: number;
      label: string;
      isFirst: boolean;
      isCollapsible: boolean;
      isExpanded: boolean;
    }
  | {
      type: 'automations-section-header';
      count: number;
      isExpanded: boolean;
      isFirst: boolean;
    }
  | { type: 'automation'; automation: Automation }
  | { type: 'session'; session: AgentSessionResponse; bucket: WorkspaceAgentSessionBucket }
  | { type: 'onboarding-review'; placement: WorkspaceAgentOnboardingReviewTaskPlacement }
  | { type: 'load-more'; scope: 'automations' | 'archived' | 'live' };

export const isWorkspaceAgentMyTasksItemRow = (row: WorkspaceAgentSessionVirtualRow | undefined): boolean =>
  row?.type === 'session' || row?.type === 'onboarding-review' || row?.type === 'automation';

export type BuildWorkspaceAgentSessionVirtualRowsParams = {
  automations: Automation[];
  hasNextAutomationsPage: boolean;
  hasNextLivePage: boolean;
  hasNextArchivedPage: boolean;
  isArchivedSectionExpanded?: boolean;
  isAutomationsSectionExpanded?: boolean;
  onboardingReviewTaskPlacement: WorkspaceAgentOnboardingReviewTaskPlacement | undefined;
  sessions: AgentSessionResponse[];
  showAutomationsSection: boolean;
};

const LIVE_TASK_BUCKETS: WorkspaceAgentSessionBucket[] = ['needs-response', 'in-progress', 'all-tasks'];

export const buildWorkspaceAgentSessionVirtualRows = ({
  automations,
  hasNextAutomationsPage,
  hasNextLivePage,
  hasNextArchivedPage,
  isArchivedSectionExpanded = false,
  isAutomationsSectionExpanded = false,
  onboardingReviewTaskPlacement,
  sessions,
  showAutomationsSection,
}: BuildWorkspaceAgentSessionVirtualRowsParams): WorkspaceAgentSessionVirtualRow[] => {
  const isArchivedReviewTask = onboardingReviewTaskPlacement === 'archived';
  const byBucket = new Map<WorkspaceAgentSessionBucket, AgentSessionResponse[]>(
    WORKSPACE_AGENT_SESSION_TASK_SECTIONS.map((section) => [section.bucket, []]),
  );

  for (const session of sessions) {
    const bucket = classifyWorkspaceAgentSessionBucket(session);
    byBucket.get(bucket)?.push(session);
  }

  const archivedItems = byBucket.get('archived') ?? [];
  const hasArchivedSection = archivedItems.length > 0 || isArchivedReviewTask;

  const liveSections = LIVE_TASK_BUCKETS.map((bucket) => ({
    bucket,
    items: byBucket.get(bucket) ?? [],
    label: WORKSPACE_AGENT_SESSION_TASK_SECTIONS.find((section) => section.bucket === bucket)?.label ?? bucket,
  })).filter((group) => group.items.length > 0);

  const rows: WorkspaceAgentSessionVirtualRow[] = [];
  let isFirstSectionHeader = onboardingReviewTaskPlacement !== 'active';

  liveSections.forEach(({ bucket, items, label }) => {
    rows.push({
      type: 'section-header',
      bucket,
      count: items.length,
      label,
      isFirst: isFirstSectionHeader,
      isCollapsible: false,
      isExpanded: true,
    });
    isFirstSectionHeader = false;

    for (const session of items) {
      rows.push({ type: 'session', session, bucket });
    }
  });

  const shouldPlaceLiveLoadMoreBeforeTrailingSections =
    hasNextLivePage && (showAutomationsSection || hasArchivedSection);

  if (shouldPlaceLiveLoadMoreBeforeTrailingSections) {
    rows.push({ type: 'load-more', scope: 'live' });
  }

  if (showAutomationsSection) {
    rows.push({
      type: 'automations-section-header',
      count: automations.length,
      isExpanded: isAutomationsSectionExpanded,
      isFirst: isFirstSectionHeader,
    });
    isFirstSectionHeader = false;

    if (isAutomationsSectionExpanded) {
      for (const automation of automations) {
        rows.push({ type: 'automation', automation });
      }

      if (hasNextAutomationsPage) {
        rows.push({ type: 'load-more', scope: 'automations' });
      }
    }
  }

  if (hasArchivedSection) {
    const archivedReviewTaskCount = isArchivedReviewTask ? 1 : 0;

    rows.push({
      type: 'section-header',
      bucket: 'archived',
      count: archivedItems.length + archivedReviewTaskCount,
      label: 'Archived',
      isFirst: isFirstSectionHeader,
      isCollapsible: true,
      isExpanded: isArchivedSectionExpanded,
    });
    isFirstSectionHeader = false;

    if (isArchivedSectionExpanded) {
      for (const session of archivedItems) {
        rows.push({ type: 'session', session, bucket: 'archived' });
      }

      if (archivedReviewTaskCount === 1) {
        rows.push({ type: 'onboarding-review', placement: 'archived' });
      }

      if (hasNextArchivedPage) {
        rows.push({ type: 'load-more', scope: 'archived' });
      }
    }
  }

  if (!shouldPlaceLiveLoadMoreBeforeTrailingSections && hasNextLivePage) {
    rows.push({ type: 'load-more', scope: 'live' });
  }

  if (onboardingReviewTaskPlacement === 'active') {
    rows.unshift({ type: 'onboarding-review', placement: 'active' });
  }

  return rows;
};

export const estimateWorkspaceAgentSessionVirtualRowSize = (
  row: WorkspaceAgentSessionVirtualRow,
  nextRow?: WorkspaceAgentSessionVirtualRow,
): number => {
  switch (row.type) {
    case 'section-header':
    case 'automations-section-header':
      return row.isFirst
        ? WORKSPACE_AGENT_MY_TASKS_SECTION_HEADER_HEIGHT
        : WORKSPACE_AGENT_MY_TASKS_SECTION_HEADER_WITH_GAP_HEIGHT;
    case 'automation':
      return nextRow?.type === 'automation'
        ? WORKSPACE_AGENT_MY_TASKS_COMPACT_ROW_TOTAL_HEIGHT
        : WORKSPACE_AGENT_MY_TASKS_COMPACT_ROW_HEIGHT;
    case 'session':
    case 'onboarding-review':
      return isWorkspaceAgentMyTasksItemRow(nextRow)
        ? WORKSPACE_AGENT_MY_TASKS_ROW_TOTAL_HEIGHT
        : WORKSPACE_AGENT_MY_TASKS_ROW_HEIGHT;
    case 'load-more':
      return WORKSPACE_AGENT_MY_TASKS_LOAD_MORE_ROW_HEIGHT;
  }
};
